
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function useStripePayment() {
  const [isLoading, setIsLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const createPaymentIntent = async ({
    businessId,
    serviceId,
    amount,
    tipAmount,
    customerInfo,
    bookingId,
  }: {
    businessId?: string;
    serviceId?: string;
    amount: number;
    tipAmount: number;
    customerInfo: {
      customerName: string;
      customerEmail: string;
      customerPhone: string;
    };
    bookingId?: string;
  }): Promise<{
    clientSecret: string;
    stripeCustomerId?: string;
    paymentIntentId?: string;
  }> => {
    if (!customerInfo.customerName || !customerInfo.customerEmail) {
      throw new Error("Please provide all contact information");
    }
    
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('stripe-connect', {
        body: {
          action: 'createPayment',
          businessId,
          serviceId,
          amount: amount || 0,
          tipAmount,
          customerEmail: customerInfo.customerEmail,
          customerName: customerInfo.customerName,
          customerPhone: customerInfo.customerPhone,
          bookingId,
        }
      });

      if (error) {
        // Edge function 4xx errors: actual message is in data.error, not error.message
        const message = (data as any)?.error || error.message || "Failed to create payment";
        console.error("Payment intent creation failed:", message);
        throw new Error(message);
      }

      if (!data?.clientSecret) {
        throw new Error("Payment service returned an invalid response. Please try again.");
      }

      const { clientSecret, stripeCustomerId, paymentIntentId } = data;
      setClientSecret(clientSecret);

      return {
        clientSecret,
        stripeCustomerId,
        paymentIntentId,
      };
    } catch (error: any) {
      console.error("Error creating payment intent:", error);
      throw error; // Let StripePaymentForm handle user notification with the real message
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayment = async ({
    businessId,
    serviceId,
    amount,
    tipAmount,
    customerInfo,
  }: {
    businessId?: string;
    serviceId?: string;
    amount: number;
    tipAmount: number;
    customerInfo: {
      customerName: string;
      customerEmail: string;
      customerPhone: string;
    };
  }) => {
    // DISABLED: This legacy redirect flow (/checkout/:clientSecret) is replaced by inline Stripe Elements
    // Use createPaymentIntent instead for inline payment flow
    console.error('handlePayment is DISABLED - this function redirects to /checkout/:clientSecret which no longer exists. Use createPaymentIntent for inline Elements flow.');
    return false;

    console.log('handlePayment function called with:', { businessId, serviceId, amount, tipAmount, customerInfo });
    
    if (!customerInfo.customerName || !customerInfo.customerEmail) {
      console.error('Missing customer information:', customerInfo);
      toast.error("Please provide all contact information");
      return false;
    }
    
    console.log('Customer info validation passed, starting payment process...');
    
    // Check current auth status
    const { data: { session }, error: authError } = await supabase.auth.getSession();
    console.log('Current auth session:', session ? 'Authenticated' : 'Anonymous', authError);
    
    // Test basic Supabase connectivity
    try {
      console.log('Testing Supabase connectivity...');
      const { data: testData, error: testError } = await supabase
        .from('business_profiles')
        .select('id')
        .limit(1);
      console.log('Supabase test result:', { testData, testError });
    } catch (testErr) {
      console.error('Supabase connectivity test failed:', testErr);
    }
    
    setIsLoading(true);
    
    try {
      console.log('Calling stripe-connect function with:', {
        action: 'createPayment',
        businessId,
        serviceId,
        amount: amount || 0,
        tipAmount,
        customerEmail: customerInfo.customerEmail,
        customerName: customerInfo.customerName,
        customerPhone: customerInfo.customerPhone || "",
      });

      // Add timeout to the function call
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Function call timeout after 30 seconds')), 30000)
      );

      const functionPromise = supabase.functions.invoke('stripe-connect', {
        body: {
          action: 'createPayment',
          businessId,
          serviceId,
          amount: amount || 0,
          tipAmount,
          customerEmail: customerInfo.customerEmail,
          customerName: customerInfo.customerName,
          customerPhone: customerInfo.customerPhone || "",
          returnUrl: window.location.href + '?payment_success=true',
        }
      });

      let { data, error } = await Promise.race([functionPromise, timeoutPromise]) as any;

      // If error, try to extract response body using fetch
      if (error && !data) {
        try {
          const rawResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-connect`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              action: 'createPayment',
              businessId,
              serviceId,
              amount: amount || 0,
              tipAmount,
              customerEmail: customerInfo.customerEmail,
              customerName: customerInfo.customerName,
              customerPhone: customerInfo.customerPhone || "",
              returnUrl: window.location.href + '?payment_success=true',
            })
          });

          const responseBody = await rawResponse.json();
          console.error('Raw response status:', rawResponse.status);
          console.error('Raw response body:', responseBody);

          if (responseBody.error) {
            throw new Error(responseBody.error);
          }
        } catch (fetchError: any) {
          console.error('Fetch error:', fetchError);
          throw fetchError;
        }
      }

      console.log('Stripe function response:', { data, error });

      if (error) {
        console.error('Stripe function returned error:', error);

        // Extract actual error message from response body if available
        if (error && typeof error === 'object') {
          console.error('Error details:', JSON.stringify(error, null, 2));

          // Try to get the underlying response
          if ((error as any).context?.body) {
            console.error('Response body:', (error as any).context.body);
          }
        }

        throw error;
      }

      if (!data || !data.clientSecret) {
        console.error('Invalid response from stripe function:', data);
        throw new Error('Invalid response from payment service');
      }

      const { clientSecret } = data;
      
      console.log('Redirecting to checkout with clientSecret:', clientSecret.substring(0, 20) + '...');
      window.location.href = `${window.location.origin}/checkout/${encodeURIComponent(clientSecret)}`;
      return true;
    } catch (error: any) {
      console.error("Error processing payment:", error);
      toast.error(error.message || "Failed to process payment");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { 
    handlePayment, 
    createPaymentIntent,
    clientSecret,
    isLoading 
  };
}
